/**
 * URL utilities for encoding/decoding form data in URLs
 * Uses human-readable query parameters
 */

/**
 * Generate a shareable URL with human-readable query parameters
 * @param formData - The form values to encode
 * @param pathname - The current page path (e.g., '/steel/')
 * @returns Full URL with ?key=value&key2=value2...
 */
export type ShareableFormData = Record<string, unknown>;

export function generateShareableURL(formData: ShareableFormData, pathname: string): string {
  const params = new URLSearchParams();

  // Convert all form values to query parameters
  for (const [key, value] of Object.entries(formData)) {
    if (value !== null && value !== undefined && value !== '') {
      params.append(key, String(value));
    }
  }

  const origin = window.location.origin;
  const path = pathname.endsWith('/') ? pathname : `${pathname}/`;

  return `${origin}${path}?${params.toString()}`;
}

/**
 * Parse URL parameters and extract form data
 * Reads all query parameters and returns them as an object
 * Converts numeric strings to numbers for dropdown fields
 *
 * @returns Parsed form data object or null if no data
 */
export function parseURLFormData(): ShareableFormData | null {
  const params = new URLSearchParams(window.location.search);

  if (params.toString() === '') {
    return null;
  }

  const data: ShareableFormData = {};

  // Known numeric fields (for dropdowns and numeric inputs that need actual numbers)
  const numericFields = ['orientation', 'supports', 'sectionType', 'direction', 'restrained'];

  for (const [key, value] of params.entries()) {
    if (numericFields.includes(key)) {
      // Convert to number for dropdown fields
      const num = Number(value);
      data[key] = isNaN(num) ? value : num;
    } else {
      // Keep as string for text inputs
      data[key] = value;
    }
  }

  return Object.keys(data).length > 0 ? data : null;
}

/**
 * Copy text to clipboard and return success status.
 *
 * Notes:
 * - Uses the modern Clipboard API when available (preferred).
 * - Falls back to the deprecated `document.execCommand('copy')` only when needed,
 *   because some older browsers / restricted contexts still rely on it.
 * - Clipboard API typically requires a secure context (HTTPS/localhost) and a user gesture.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  // Prefer modern API
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      // Continue to fallback below
      console.warn('Clipboard API failed, trying legacy fallback:', err);
    }
  }

  // Legacy fallback (deprecated but sometimes necessary)
  try {
    const textArea = document.createElement('textarea');
    textArea.value = text;

    // Avoid scrolling & make it effectively invisible
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.opacity = '0';
    textArea.style.pointerEvents = 'none';
    textArea.setAttribute('readonly', '');

    document.body.appendChild(textArea);

    // iOS needs explicit range sometimes
    textArea.focus();
    textArea.select();
    textArea.setSelectionRange(0, text.length);

    const success = document.execCommand('copy');

    document.body.removeChild(textArea);
    return success;
  } catch (fallbackErr) {
    console.error('Fallback copy failed:', fallbackErr);
    return false;
  }
}
